import { isDefined } from "@scalar/helpers/array/is-defined";
import { getResolvedRef } from "@scalar/workspace-store/helpers/get-resolved-ref";
const MAX_LEVELS_DEEP = 10;
const MAX_PROPERTIES = 10;
const DEFAULT_ADDITIONAL_PROPERTIES_NAME = "propertyName*";
const currentISOString = (/* @__PURE__ */ new Date()).toISOString();
const currentDateString = currentISOString.split("T")[0];
const currentTimeString = currentISOString.split("T")[1].split(".")[0];
const genericExampleValues = {
  // 'date-time': '1970-01-01T00:00:00Z',
  "date-time": currentISOString,
  // 'date': '1970-01-01',
  "date": currentDateString,
  "email": "hello@example.com",
  "hostname": "example.com",
  // https://tools.ietf.org/html/rfc6531#section-3.3
  "idn-email": "jane.doe@example.com",
  // https://tools.ietf.org/html/rfc5890#section-2.3.2.3
  "idn-hostname": "example.com",
  "ipv4": "127.0.0.1",
  "ipv6": "51d4:7fab:bfbf:b7d7:b2cb:d4b4:3dad:d998",
  "iri-reference": "/entitiy/1",
  // https://tools.ietf.org/html/rfc3987
  "iri": "https://example.com/entity/123",
  "json-pointer": "/nested/objects",
  "password": "super-secret",
  "regex": "/[a-z]/",
  // https://tools.ietf.org/html/draft-handrews-relative-json-pointer-01
  "relative-json-pointer": "1/nested/objects",
  // full-time in https://tools.ietf.org/html/rfc3339#section-5.6
  // 'time': '00:00:00Z',
  "time": currentTimeString,
  // either a URI or relative-reference https://tools.ietf.org/html/rfc3986#section-4.1
  "uri-reference": "../folder",
  "uri-template": "https://example.com/{id}",
  "uri": "https://example.com",
  "uuid": "123e4567-e89b-12d3-a456-426614174000",
  "object-id": "6592008029c8c3e4dc76256c"
};
function guessFromFormat(schema, makeUpRandomData = false, fallback = "") {
  if (schema.format === "binary") {
    return new File([""], "filename");
  }
  return makeUpRandomData ? genericExampleValues[schema.format ?? ""] ?? fallback : "";
}
const resultCache = /* @__PURE__ */ new WeakMap();
function cache(schema, result) {
  if (typeof result !== "object" || result === null) {
    return result;
  }
  resultCache.set(schema, result);
  return result;
}
const getExampleFromSchema = (_schema, options, level = 0, parentSchema, name) => {
  const schema = getResolvedRef(_schema);
  if (!isDefined(schema)) {
    return void 0;
  }
  if (resultCache.has(schema)) {
    return resultCache.get(schema);
  }
  if (level > MAX_LEVELS_DEEP) {
    return "[Max Depth Exceeded]";
  }
  const makeUpRandomData = !!options?.emptyString;
  if (schema.deprecated) {
    return void 0;
  }
  if (options?.mode === "write" && schema.readOnly || options?.mode === "read" && schema.writeOnly) {
    return void 0;
  }
  if (schema["x-variable"]) {
    const value = options?.variables?.[schema["x-variable"]];
    if (value !== void 0) {
      if (schema.type === "number" || schema.type === "integer") {
        const numValue = Number.parseInt(value, 10);
        return cache(schema, numValue);
      }
      return cache(schema, value);
    }
  }
  if (Array.isArray(schema.examples) && schema.examples.length > 0) {
    return cache(schema, schema.examples[0]);
  }
  if (schema.example !== void 0) {
    return cache(schema, schema.example);
  }
  if (schema.default !== void 0) {
    return cache(schema, schema.default);
  }
  if (schema.const !== void 0) {
    return cache(schema, schema.const);
  }
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return cache(schema, schema.enum[0]);
  }
  const isObjectOrArray = schema.type === "object" || schema.type === "array" || !!schema.allOf?.at?.(0) || !!schema.anyOf?.at?.(0) || !!schema.oneOf?.at?.(0);
  if (!isObjectOrArray && options?.omitEmptyAndOptionalProperties === true) {
    const isRequired = schema.required === true || parentSchema?.required === true || parentSchema?.required?.includes(name ?? schema.title ?? "");
    if (!isRequired) {
      return void 0;
    }
  }
  if (schema.type === "object" || schema.properties !== void 0) {
    const response = {};
    let propertyCount = 0;
    if (schema.properties !== void 0) {
      const propertyNames = Object.keys(schema.properties);
      const maxProps = level > 3 ? MAX_PROPERTIES : propertyNames.length;
      for (const propertyName of propertyNames.slice(0, maxProps)) {
        const property = getResolvedRef(schema.properties[propertyName]);
        if (!property) {
          continue;
        }
        const propertyXmlTagName = options?.xml ? property?.xml?.name : void 0;
        const value = getExampleFromSchema(property, options, level + 1, schema, propertyName);
        if (typeof value !== "undefined") {
          response[propertyXmlTagName ?? propertyName] = value;
          propertyCount++;
        }
      }
      if (level > 3 && propertyCount >= MAX_PROPERTIES && propertyNames.length > MAX_PROPERTIES) {
        response["..."] = "[Additional Properties Truncated]";
      }
    }
    if (schema.patternProperties !== void 0) {
      for (const pattern of Object.keys(schema.patternProperties)) {
        const property = getResolvedRef(schema.patternProperties[pattern]);
        if (!property) {
          continue;
        }
        response[pattern] = getExampleFromSchema(property, options, level + 1, schema, pattern);
      }
    }
    if (schema.additionalProperties !== void 0) {
      const anyTypeIsValid = (
        // true
        schema.additionalProperties === true || // or an empty object {}
        typeof schema.additionalProperties === "object" && !Object.keys(schema.additionalProperties).length
      );
      const additionalProperties = getResolvedRef(schema.additionalProperties);
      const additionalPropertiesName = typeof additionalProperties === "object" && additionalProperties["x-additionalPropertiesName"] && typeof additionalProperties["x-additionalPropertiesName"] === "string" && additionalProperties["x-additionalPropertiesName"].trim().length > 0 ? `${additionalProperties["x-additionalPropertiesName"].trim()}*` : DEFAULT_ADDITIONAL_PROPERTIES_NAME;
      if (anyTypeIsValid) {
        response[additionalPropertiesName] = "anything";
      } else if (typeof additionalProperties === "object") {
        response[additionalPropertiesName] = getExampleFromSchema(additionalProperties, options, level + 1);
      }
    }
    if (schema.anyOf?.[0]) {
      Object.assign(response, getExampleFromSchema(getResolvedRef(schema.anyOf[0]), options, level + 1));
    } else if (schema.oneOf?.[0] !== void 0) {
      Object.assign(response, getExampleFromSchema(getResolvedRef(schema.oneOf[0]), options, level + 1));
    } else if (schema.allOf?.[0]) {
      Object.assign(
        response,
        ...schema.allOf.filter(isDefined).map((item) => getExampleFromSchema(getResolvedRef(item), options, level + 1, schema))
      );
    }
    if (options?.xml && schema.xml?.name && level === 0) {
      const wrappedResponse = {};
      wrappedResponse[schema.xml.name] = response;
      return cache(schema, wrappedResponse);
    }
    return cache(schema, response);
  }
  if (schema.type === "array" || schema.items !== void 0) {
    const items = getResolvedRef(schema.items);
    const itemsXmlTagName = items?.xml?.name;
    const wrapItems = !!(options?.xml && schema.xml?.wrapped && itemsXmlTagName);
    if (schema.example !== void 0) {
      return cache(schema, wrapItems ? { [itemsXmlTagName]: schema.example } : schema.example);
    }
    if (items) {
      if (items.allOf) {
        const allOf = items.allOf.filter(isDefined);
        const firstItem = getResolvedRef(allOf[0]);
        if (firstItem?.type === "object") {
          const combined = { type: "object", allOf };
          const mergedExample = getExampleFromSchema(combined, options, level + 1, schema);
          return cache(schema, wrapItems ? [{ [itemsXmlTagName]: mergedExample }] : [mergedExample]);
        }
        const examples = allOf.map((item) => getExampleFromSchema(getResolvedRef(item), options, level + 1, schema)).filter(isDefined);
        return cache(schema, wrapItems ? examples.map((example) => ({ [itemsXmlTagName]: example })) : examples);
      }
      if (items.anyOf || items.oneOf) {
        const ruleItems = items.anyOf || items.oneOf;
        if (ruleItems && ruleItems.length > 0) {
          const firstItem = ruleItems[0];
          if (firstItem) {
            const exampleFromRule = getExampleFromSchema(getResolvedRef(firstItem), options, level + 1, schema);
            return cache(schema, wrapItems ? [{ [itemsXmlTagName]: exampleFromRule }] : [exampleFromRule]);
          }
        }
      }
    }
    const isObject = items?.type === "object" || items?.properties !== void 0;
    const isArray = items?.type === "array" || items?.items !== void 0;
    if (items?.type || isObject || isArray) {
      const exampleFromSchema = getExampleFromSchema(items, options, level + 1);
      return cache(schema, wrapItems ? [{ [itemsXmlTagName]: exampleFromSchema }] : [exampleFromSchema]);
    }
    return cache(schema, []);
  }
  const exampleValues = {
    string: guessFromFormat(schema, makeUpRandomData, options?.emptyString),
    boolean: true,
    integer: schema.minimum ?? 1,
    number: schema.minimum ?? 1,
    array: []
  };
  if (schema.type && !Array.isArray(schema.type) && exampleValues[schema.type] !== void 0) {
    return cache(schema, exampleValues[schema.type]);
  }
  const discriminateSchema = schema.oneOf || schema.anyOf;
  if (Array.isArray(discriminateSchema) && discriminateSchema.length > 0) {
    const firstNonNullItem = discriminateSchema.map((item) => getResolvedRef(item)).find((item) => item.type !== "null");
    if (firstNonNullItem) {
      const result = getExampleFromSchema(firstNonNullItem, options, level + 1);
      return cache(schema, result);
    }
    return cache(schema, null);
  }
  if (Array.isArray(schema.allOf)) {
    let example = null;
    schema.allOf.forEach((allOfItem) => {
      const newExample = getExampleFromSchema(getResolvedRef(allOfItem), options, level + 1);
      example = typeof newExample === "object" && typeof example === "object" ? {
        ...example ?? {},
        ...newExample
      } : Array.isArray(newExample) && Array.isArray(example) ? [...example ?? {}, ...newExample] : newExample;
    });
    return cache(schema, example);
  }
  if (Array.isArray(schema.type)) {
    if (schema.type.includes("null")) {
      return cache(schema, null);
    }
    const exampleValue = exampleValues[schema.type[0] ?? ""];
    if (exampleValue !== void 0) {
      return cache(schema, exampleValue);
    }
  }
  return cache(schema, null);
};
export {
  getExampleFromSchema
};
//# sourceMappingURL=get-example-from-schema.js.map
